import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useState, useEffect } from 'react';

// No fallback — null forces callers to handle the unauthenticated/unresolved case explicitly.
const DEFAULT_SCHOOL_ID = null;

/**
 * Check if the current user is a solo user (not associated with a school)
 */
export async function isSoloUser() {
  const user = auth.currentUser;
  if (!user) {
    return false;
  }

  try {
    // Check custom claims first
    const tokenResult = await user.getIdTokenResult();
    if (tokenResult.claims.userType === 'solo' || tokenResult.claims.role === 'solo_user') {
      return true;
    }

    // Fallback: check user document
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.userType === 'solo' || userData.role === 'solo_user';
    }

    return false;
  } catch (error) {
    console.error('Error checking if solo user:', error);
    return false;
  }
}

/**
 * Hook to check if current user is a solo user
 */
export function useIsSoloUser() {
  const [user] = useAuthState(auth);
  const [isSolo, setIsSolo] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkSoloUser() {
      if (!user) {
        setIsSolo(false);
        setLoading(false);
        return;
      }

      try {
        const result = await isSoloUser();
        setIsSolo(result);
      } catch (error) {
        console.error('Error in useIsSoloUser:', error);
        setIsSolo(false);
      } finally {
        setLoading(false);
      }
    }

    checkSoloUser();
  }, [user]);

  return { isSoloUser: isSolo, loading };
}

/**
 * Hook to get the current user's school ID
 * First checks Firebase Auth custom claims, then falls back to user document
 */
export function useSchoolId() {
  const [user] = useAuthState(auth);
  const [schoolId, setSchoolId] = useState(DEFAULT_SCHOOL_ID);
  const [loadingSchoolId, setLoadingSchoolId] = useState(true);

  useEffect(() => {
    async function getSchoolId() {
      if (!user) {
        setSchoolId(DEFAULT_SCHOOL_ID);
        setLoadingSchoolId(false);
        return;
      }

      try {
        // First check if schoolId is in custom claims
        const tokenResult = await user.getIdTokenResult();
        if (tokenResult.claims.schoolId) {
          setSchoolId(tokenResult.claims.schoolId);
          setLoadingSchoolId(false);
          return;
        }

        // Fallback: get schoolId from user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.schoolId) {
            setSchoolId(userData.schoolId);
          } else {
            setSchoolId(DEFAULT_SCHOOL_ID);
          }
        } else {
          setSchoolId(DEFAULT_SCHOOL_ID);
        }
      } catch (error) {
        console.error('Error getting school ID:', error);
        setSchoolId(DEFAULT_SCHOOL_ID);
      } finally {
        setLoadingSchoolId(false);
      }
    }

    getSchoolId();
  }, [user]);

  return { schoolId, loading: loadingSchoolId };
}

/**
 * Utility function to get school ID from user context
 * Used in components that don't need reactive updates
 */
export async function getCurrentSchoolId() {
  const user = auth.currentUser;
  if (!user) {
    return DEFAULT_SCHOOL_ID;
  }

  try {
    // First check custom claims
    const tokenResult = await user.getIdTokenResult();
    if (tokenResult.claims.schoolId) {
      return tokenResult.claims.schoolId;
    }

    // Fallback: get from user document
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.schoolId || DEFAULT_SCHOOL_ID;
    }

    return DEFAULT_SCHOOL_ID;
  } catch (error) {
    console.error('Error getting school ID:', error);
    return DEFAULT_SCHOOL_ID;
  }
}

export { DEFAULT_SCHOOL_ID };

/**
 * Hook to get the current school's phase setting (primary/secondary/all-through)
 * This is used to filter KB items based on school type
 */
export function useSchoolPhase() {
  const [user] = useAuthState(auth);
  const { schoolId } = useSchoolId();
  const [schoolPhase, setSchoolPhase] = useState('all-through'); // Default to show all
  const [loadingPhase, setLoadingPhase] = useState(true);

  useEffect(() => {
    async function getSchoolPhase() {
      if (!user || !schoolId) {
        setSchoolPhase('all-through');
        setLoadingPhase(false);
        return;
      }

      try {
        // Get school document to check phase setting
        const schoolDoc = await getDoc(doc(db, 'schools', schoolId));
        if (schoolDoc.exists()) {
          const schoolData = schoolDoc.data();
          setSchoolPhase(schoolData.phase || schoolData.schoolPhase || 'all-through');
        } else {
          setSchoolPhase('all-through');
        }
      } catch (error) {
        console.error('Error getting school phase:', error);
        setSchoolPhase('all-through');
      } finally {
        setLoadingPhase(false);
      }
    }

    getSchoolPhase();
  }, [user, schoolId]);

  return { schoolPhase, loading: loadingPhase };
}

/**
 * Helper function to filter activities by school phase
 * @param {Array} activities - Array of KB items
 * @param {string} schoolPhase - 'primary', 'secondary', or 'all-through'
 * @returns {Array} Filtered activities appropriate for the school phase
 */
export function filterActivitiesByPhase(activities, schoolPhase) {
  if (!schoolPhase || schoolPhase === 'all-through') {
    return activities;
  }

  return activities.filter(activity => {
    // Check if activity has explicit phase field
    if (activity.phase) {
      return activity.phase === schoolPhase || activity.phase === 'both';
    }

    // Fallback to age range for primary/secondary distinction
    const minAge = activity.ageRange?.min || 0;
    const maxAge = activity.ageRange?.max || 19;

    if (schoolPhase === 'primary') {
      // Primary: ages 4-11, exclude activities that are secondary-only
      return minAge < 12;
    } else if (schoolPhase === 'secondary') {
      // Secondary: ages 11-16, exclude activities that are primary-only
      return maxAge >= 11;
    }

    return true;
  });
}
